// Phase 6.C — 시안 design/v3/SchedulePage.jsx:279-326 DayHeader port.
// 작업일정 페이지 상단 헤더 (날짜·인원수·미응답 배지·전체발송).
// 2026-05-03 Track A 잔여 — 직원 수 chip = SCH_STAFF.length 하드코딩 (=0) → 실 staff API
// 연결 (staffControllerList, queryKey ['staff','list']). 회사별 격리는 백엔드 RLS 가
// 보장. status='active' 만 count (invited/disabled 제외 = '현재 활동 인원' 명세).
// 2026-05-03 정상화 #1 — chip 라벨 "직원" → "인원" 으로 정정. /v1/staff 가 이미 모든
// role (owner + staff) 의 membership 을 반환 (membership.findMany where: {} + RLS) 하므로
// active count = "owner + active staff" = "회사 소속 활동 인원" 의미와 일치한다.
// (= ALGORITHM_FLOW §20 회사별 격리 인원 카운트 spec)

import { useQuery } from '@tanstack/react-query';
import { staffControllerList } from '@dvvb/sdk';
import { Icon, Button, useToast } from '../../components/primitives';
import { unwrap } from '../../lib/envelope';
import { SCHEDULE_SECTIONS, findSection } from './data';

interface StaffListItem {
  id: string;
  status: 'active' | 'invited' | 'disabled';
}

interface StaffListEnvelope {
  items: StaffListItem[];
  count: number;
}

export function ScheduleDayHeader({ sectionId }: { sectionId?: string }) {
  const toast = useToast();
  // Settings/staff 와 동일한 queryKey ['staff','list'] 공유 — 직원 추가/삭제 시 invalidate
  // 가 자동 전파되어 chip 도 갱신된다.
  const staffQ = useQuery({
    queryKey: ['staff', 'list'],
    queryFn: async () => {
      const r = await staffControllerList({ query: { limit: '500' } as never });
      return unwrap<StaffListEnvelope>(r);
    },
  });
  const totalStaff = (staffQ.data?.items ?? []).filter((s) => s.status === 'active').length;
  const closing = SCHEDULE_SECTIONS.find((s) => s.isResponse);
  const pendingResp = closing
    ? closing.tasks.filter((t) => !t.answered).length
    : 0;
  const current = sectionId ? findSection(sectionId) : null;
  const title = current ? current.title : '작업일정';
  const sub = current
    ? current.sub
    : '공통 · 오전 · 오후 · 전처리(재고연결) · 퇴근후 — 한 페이지에 카테고리형';
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const dayKor = ['일', '월', '화', '수', '목', '금', '토'][today.getDay()];
  const dateLabel = `${yyyy}-${mm}-${dd} (${dayKor})`;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        padding: '0 0 8px',
      }}
    >
      <div style={{ flex: 1, minWidth: 200 }}>
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            color: 'var(--fg-1)',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}
        >
          {title}{' '}
          <span style={{ color: 'var(--fg-3)', fontWeight: 500, fontSize: 18 }}>
            · {dateLabel}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 4 }}>{sub}</div>
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 11px',
            borderRadius: 999,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-weak)',
            fontSize: 12,
            color: 'var(--fg-2)',
          }}
        >
          <Icon name="users" size={13} color="var(--fg-3)" />
          인원 <b style={{ color: 'var(--fg-1)' }}>{totalStaff}</b>명
        </span>
        {pendingResp > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 11px',
              borderRadius: 999,
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning)',
              fontSize: 12,
              color: 'var(--warning)',
              fontWeight: 600,
            }}
          >
            <Icon name="alert" size={13} color="var(--warning)" />
            미응답 {pendingResp}건
          </span>
        )}
        <Button
          variant="primary"
          size="sm"
          onClick={() =>
            toast.info(
              '전체 발송은 Phase 7 메일·SMS 인프라 도입 후 활성됩니다. (Mock)',
            )
          }
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Icon name="send" size={13} color="var(--primary-strong)" />
            전체 발송
          </span>
        </Button>
      </div>
    </div>
  );
}
